
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ImageUpload } from './ImageUpload';

interface AddPortfolioItemProps {
  onAdd: () => void;
}

const STORAGE_KEY = 'portfolio_form_draft';

export const AddPortfolioItem: React.FC<AddPortfolioItemProps> = ({ onAdd }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    projectUrl: ''
  });

  // Load saved form data from localStorage when component mounts
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        // If there's saved data, automatically open the form
        if (parsedData.title || parsedData.description || parsedData.imageUrl || parsedData.projectUrl) {
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (isOpen && (formData.title || formData.description || formData.imageUrl || formData.projectUrl)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('portfolio_items')
        .insert({
          user_id: user.id,
          title: formData.title,
          description: formData.description || null,
          image_url: formData.imageUrl || null,
          project_url: formData.projectUrl || null
        });

      if (error) throw error;

      toast.success('Portfolio item added successfully');
      
      // Clear saved form data after successful submission
      localStorage.removeItem(STORAGE_KEY);
      setFormData({ title: '', description: '', imageUrl: '', projectUrl: '' });
      setIsOpen(false);
      onAdd();
    } catch (error) {
      console.error('Error adding portfolio item:', error);
      toast.error('Failed to add portfolio item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear saved form data when explicitly canceling
    localStorage.removeItem(STORAGE_KEY);
    setFormData({ title: '', description: '', imageUrl: '', projectUrl: '' });
    setIsOpen(false);
  };

  const handleImageChange = (imageUrl: string | null) => {
    setFormData(prev => ({ ...prev, imageUrl: imageUrl || '' }));
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) {
    return (
      <Button 
        onClick={() => setIsOpen(true)}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Portfolio Item
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Add Portfolio Item</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
              placeholder="Project title"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of your project"
              rows={3}
            />
          </div>
          
          <ImageUpload
            currentImageUrl={formData.imageUrl || undefined}
            onImageChange={handleImageChange}
          />
          
          <div>
            <Label htmlFor="projectUrl">Project URL</Label>
            <Input
              id="projectUrl"
              type="url"
              value={formData.projectUrl}
              onChange={(e) => handleInputChange('projectUrl', e.target.value)}
              placeholder="https://example.com/project"
            />
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isSubmitting || !formData.title}>
              {isSubmitting ? 'Adding...' : 'Add Item'}
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
